import sliderStyle from '../slider.module.scss';

import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as WarningIcon } from "assets/images/icons/icn_btn_authenticate_red.svg";

import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import { authStateOption, scopeLevelOption, otherTimeout } from "../../../const";

import { getDefaultTextFieldProp } from "../../../theme";
import { SolidButton } from "components/buttons/SolidButton";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import { getAPIKeyAndParams } from '../../../requests';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const Overview = (props) => {
  const {
    auth,
    device, changeToConfigurable, startAdoptProcessForOneDevice
  } = props;
  const { sendRequest, getData } = useDataSourceManager();

  // Status
  const [openForm, setOpenForm] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isPassword, setIsPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [socketId, setSocketId] = useState(null);

  // Method
  const toggleOpenForm = (flag) => {
    setOpenForm(flag);
  }

  const authenticate = async () => {
    if (username.length === 0 || password.length === 0) {
      return;
    }

    const newSocketId = uuidv4();
    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'authenticate');

    const param = {
      timeout: otherTimeout,
      data: [{
        layer: device.layer,
        ddpVersion: device.ddpVersion,
        destMacAddress: device.macAddress,
        destIpAddress: device.ipAddress,
        username,
        password,
      }],
    };

    if (device.layer === 3) {
      param.data[0]['ipVersion'] = device.ipVersion;
    }

    sendRequest(
      apiKey,
      newSocketId,
      placeholder,
      param,
    );

    setSocketId(newSocketId);
    setWaiting(true);
  }

  const adopt = () => {
    if (device.authStatus === authStateOption.failed) {
      return
    }

    setOpenForm(false);
    setIsAuthenticated(false);
    startAdoptProcessForOneDevice(device);
  }

  // Side effect
  useEffect(() => {
    setWaiting(false);
    setIsError(false);
    setUsername('');
    setPassword('');
    setOpenForm(false);
    setIsAuthenticated(false);
  }, [device]);

  useEffect(() => {
    const response = getData(socketId);

    if (!response) {
      return;
    }

    const { resource, data } = response;
    if (!resource) {
      return;
    }

    let result = null;
    if (checkMockup) {
      result = { code: 0, macAddress: device.macAddress, message: "Success" };
    } else {
      result = data[0];

      // Avoid error when the data is empty.
      if (!result) {
        return;
      }
    }

    if (result.code === 0) {
      setIsError(false);
      setIsAuthenticated(true);
      changeToConfigurable(device, username, password);
      setOpenForm(false);
      setUsername('');
      setPassword('');
    } else {
      setIsError(true);
      setIsAuthenticated(false);
    }

    setWaiting(false);
  }, [changeToConfigurable, socketId]);

  if (!device) {
    return null;
  }



  return (
    <>
      <Box className={sliderStyle['field-container']}>
        {/* Auth */}
        <Box className={sliderStyle['field']}>
          Auth
        </Box>
        <Box className={sliderStyle['value']}>
          {
            device.authStatus === authStateOption.failed ?
              (
                <Box className={sliderStyle['auth']}>
                  {
                    !isAuthenticated && (
                      <Box
                        className={`${sliderStyle['btn']} ${sliderStyle['warning']}`}
                        onClick={() => toggleOpenForm(!openForm)}
                      >
                        <WarningIcon />
                        Authentication Required
                      </Box>
                    )
                  }
                </Box>
              ) : (
                <>
                  <div className={sliderStyle['success']}>
                    Configurable
                  </div>
                </>
              )
          }
        </Box>
        {
          openForm && (
            <>
              <Box className={sliderStyle['field']} style={{ justifyContent: 'end' }}>
                <li>Username</li>
              </Box>
              <Box className={sliderStyle['value']}>
                <TextField
                  fullWidth
                  placeholder=""
                  type="text"
                  value={username}
                  autoComplete="new-password"
                  onChange={(e) => setUsername(e.target.value)}
                  sx={{ ...getDefaultTextFieldProp().sx }}
                />
              </Box>
              <Box className={sliderStyle['field']} style={{ justifyContent: 'end' }}>
                <li>Password</li>
              </Box>
              <Box className={sliderStyle['value']}>
                <TextField
                  fullWidth
                  placeholder=""
                  type={isPassword ? 'text' : 'password'}
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ ...getDefaultTextFieldProp().sx }}
                  InputProps={{
                    endAdornment: isPassword ? (
                      <InputAdornment position="end">
                        <ShowIcon onClick={() => setIsPassword(!isPassword)} style={{ cursor: 'pointer' }} />
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        <HideIcon onClick={() => setIsPassword(!isPassword)} style={{ cursor: 'pointer' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {
                isError && (
                  <>
                    <Box className={sliderStyle['field']}></Box>
                    <Box className={sliderStyle['value']}>
                      <div className={sliderStyle['error']}>
                        Invalid username or password
                      </div>
                    </Box>
                  </>
                )
              }
              <Box className={sliderStyle['field']}></Box>
              <Box className={sliderStyle['value']}>
                <SolidRectangleButton
                  className={`${sliderStyle['submit_btn']} ${((username.length > 0 && password.length > 0) && !waiting) ? sliderStyle['enabled_btn'] : sliderStyle['disabled_btn']}`}
                  onClick={() => authenticate()}
                >
                  Submit
                </SolidRectangleButton>
              </Box>
            </>
          )
        }

        {/* IP address */}
        <Box className={sliderStyle['field']}>
          IP Address
        </Box>
        <Box className={sliderStyle['value']}>
          {device.ipAddress}
        </Box>
        {/* MAC address */}
        <Box className={sliderStyle['field']}>
          MAC Address
        </Box>
        <Box className={sliderStyle['value']}>
          {device.macAddress}
        </Box>
        {/* Model name */}
        <Box className={sliderStyle['field']}>
          Model Name
        </Box>
        <Box className={sliderStyle['value']}>
          {device.modelName}
        </Box>
        {/* Firmware version */}
        <Box className={sliderStyle['field']}>
          Firmware Version
        </Box>
        <Box className={sliderStyle['value']}>
          {device.fwVersion}
        </Box>
        {/* Hardware version */}
        <Box className={sliderStyle['field']}>
          Hardware Version
        </Box>
        <Box className={sliderStyle['value']}>
          {device.hdVersion}
        </Box>
        {/* Controller inform URL */}
        {/* Bug: #33158 */}
        {/* <Box className={sliderStyle['field']}>
          Controller Inform URL
        </Box> */}
        {/* <Box className={sliderStyle['value']}>
          {device.controllerInformURL || '-'}
        </Box> */}
      </Box>
      {
        !device.provisioning && (
          <Box className={sliderStyle['button-container']}>
            <SolidButton
              className={`${sliderStyle['apply-btn']} ${(device.authStatus === 'passed') ? sliderStyle['enabled_btn'] : sliderStyle['disabled_btn']}`}
              onClick={adopt}
            >
              <span className={sliderStyle['text']}>Adopt</span>
            </SolidButton>
          </Box>
        )
      }
    </>
  );
}

export default Overview;